<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue';
import {
  tpgIconDraw,
  tpgIconLayersClear,
  tpgIconPadlock,
} from '@tpg/grammar-web-icons';
import { computed, ref, watch } from 'vue';

import { GrammarPrimaryButton } from '@nora-monorepo/grammar-ui';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';

import ToolListView from '#nora-ui/map/components/drawtools/tool-list/ToolListView.vue';
import ToolListViewMessage from '#nora-ui/map/components/drawtools/tool-list/ToolListViewMessage.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface Props {
  mapId: string;
}

const props = defineProps<Props>();

const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const drawToolUI = map.drawToolUI;
const drawToolManager = map.drawToolManager;

const openToolListButtonReference = ref(null);
const toolListReference = ref(null);
const activeDrawLayer = map.state.draw.activeDrawLayer;
const canDraw = drawToolManager.canDraw;
const isActiveDrawLayerLocked = drawToolManager.isActiveDrawLayerLocked;

const disabled = computed(() => {
  return activeDrawLayer.value?.type === DrawLayerTypeEnum.GEOFENCE
    || drawToolUI.isEditing.value;
});

const hasActiveOperation = computed(() => {
  return Boolean(map.ref.activeOperationId);
});

const { floatingStyles } = useFloating(
  openToolListButtonReference,
  toolListReference,
  {
    whileElementsMounted: autoUpdate,
    placement: 'right-start',
    middleware: [offset(10), flip(), shift({ padding: 5 })],
    transform: false,
  },
);

function toggleDrawMode() {
  // The popover opens regardless of whether drawing is possible: when it isn't, the popover
  // surfaces guidance (select an operation / draw layer) instead of the tool grid.
  const shouldEnableDraw = !drawToolUI.isToolListOpen.value;
  drawToolUI.setDrawToolByType(null);
  drawToolUI.isToolListOpen.value = shouldEnableDraw;
};

watch(activeDrawLayer, () => {
  if (activeDrawLayer.value && activeDrawLayer.value.type === DrawLayerTypeEnum.GEOFENCE) {
    drawToolUI.isToolListOpen.value = false;
  }
});
</script>

<template>
  <div class="flex flex-row items-start justify-start gap-4 max-w-2xl">
    <GrammarPrimaryButton
      ref="openToolListButtonReference"
      v-can="{
        permission: 'update:draw-layer.feature',
      }"
      :icon-template="tpgIconDraw"
      :disabled="disabled"
      @click="toggleDrawMode"
    />
    <div
      v-if="drawToolUI.isToolListOpen.value"
      ref="toolListReference"
      :style="floatingStyles"
      class="rounded-sm"
    >
      <ToolListViewMessage
        v-if="isActiveDrawLayerLocked"
        :map-id="mapId"
        :icon-template="tpgIconPadlock"
        heading="Locked draw layer"
        message="Unlock in layers to add drawing or object."
      />
      <ToolListView
        v-else-if="canDraw"
        :map-id="mapId"
      />
      <ToolListViewMessage
        v-else-if="hasActiveOperation"
        :map-id="mapId"
        :icon-template="tpgIconLayersClear"
        heading="No drawlayer selected"
        message="Select or create a drawlayer to add drawing or object."
      />
      <ToolListViewMessage
        v-else
        :map-id="mapId"
        :icon-template="tpgIconLayersClear"
        heading="No operation selected"
        message="Select an operation and a drawlayer to add drawing or objects."
      />
    </div>
  </div>
</template>
